/**
 * 内容编辑页(屏 4)的纯函数:区块人话、占位判定、绑定来源 chip、按 path 前缀切模板字段、
 * 点路径读写、区块排序。零 React、零请求。
 *
 * ★ 值的落点全按「模板字段路径去掉前缀」(附录 A.9):`pages.home.hero_split.cta.label` → `block.props.cta.label`,
 *   `brand.phone` → `content.brand.phone`,`pages.home.features_3.items[].desc` → `block.props.items[i].desc`。
 *   表单和预览都用 getAt / setAt 走这一条,不各自 hardcode 键名。
 */
import type { FieldSpec, SiteContent, Template } from "../api/types";

const BLOCK_LABEL: Record<string, string> = {
  hero_split: "首屏 Hero",
  features_3: "核心优势",
  product_grid: "产品精选",
  case_wall: "客户案例",
  credential_wall: "资质荣誉",
  news_list: "企业动态",
  lead_form: "联系表单",
  contact_footer: "联系页脚",
  spec_table: "参数表",
  metrics: "数据指标",
  map: "地图",
  opening_hours: "营业时间",
  countdown: "倒计时",
};

/** 区块类型码 → 人话;未知码原样(新模板上架先出英文码也比消失强)。 */
export function blockLabel(type: string): string {
  return BLOCK_LABEL[type] ?? type;
}

const PLACEHOLDER = /^\[待补:.+\]$/;

/** 附录 A.2:草稿里缺必填事实时 api 填的占位 `[待补:{label}]`;发布前校验要求零占位。 */
export function isPlaceholder(value: unknown): boolean {
  return typeof value === "string" && PLACEHOLDER.test(value);
}

/**
 * 事实字段:模板标了 `fact`,或天然是事实的类型(电话 / 地址 —— 模板漏标也按事实处理,与屏 3 清单同一条规矩)。
 * 编辑页据此上锁、不给「AI 重写」,也据此数「资料待补」。
 */
export function isFactField(field: Pick<FieldSpec, "fact" | "type">): boolean {
  return field.fact || field.type === "phone" || field.type === "address";
}

export interface PendingFact {
  /** 实例路径(`brand.icpNo` / `products[1].name` / `credentials[0]` / `pages.home.x.items[0].y`)。 */
  path: string;
  label: string;
}

/** 叶子值带占位:字符串本身是占位,或元素是对象(资质 `{assetKey, title}` 一类)且某个值是占位。 */
function leafHasPlaceholder(value: unknown): boolean {
  if (isPlaceholder(value)) return true;
  if (value != null && typeof value === "object" && !Array.isArray(value)) {
    return Object.values(value as Record<string, unknown>).some(isPlaceholder);
  }
  return false;
}

/**
 * 在 `root` 上把模式路径(可含 `[]`)展开成实例路径 + 值;根级数组字段(`credentials`)按元素展开。
 * 路径上断了 / 不是数组就没有实例,不报错。
 */
function expandInstances(root: unknown, pattern: string): Array<{ path: string; value: unknown }> {
  const at = pattern.indexOf("[]");
  if (at < 0) {
    const value = getAt(root, pattern);
    return Array.isArray(value)
      ? value.map((v, i) => ({ path: `${pattern}[${i}]`, value: v }))
      : [{ path: pattern, value }];
  }
  const base = pattern.slice(0, at);
  const rest = pattern.slice(at + 2);
  const list = getAt(root, base);
  if (!Array.isArray(list)) return [];
  return list.flatMap((_, i) => expandInstances(root, `${base}[${i}]${rest}`));
}

/**
 * 草稿里还是占位 `[待补:…]` 的**事实字段**(附录 A.2:发布前校验要求零占位)。
 * 事实值只能来自资料或用户在采集页答的题 —— 编辑页改不了它们(产品 / 案例 / 资质这里根本不给表单),
 * 页面据此提示「回到资料采集补齐」。`pages.{slug}.{type}.*` 的字段落到该页所有同型区块的 props 上找。
 */
export function placeholderFacts(
  template: Pick<Template, "schema">,
  content: SiteContent
): PendingFact[] {
  const out: PendingFact[] = [];
  for (const f of template.schema.flatMap((g) => g.fields)) {
    if (!isFactField(f)) continue;
    const m = /^pages\.([^.]+)\.([^.]+)\.(.+)$/.exec(f.path);
    const hits = m
      ? content.pages
          .filter((p) => p.slug === m[1])
          .flatMap((p) => p.blocks.filter((b) => b.type === m[2]))
          .flatMap((b) =>
            expandInstances(b.props, m[3]).map((x) => ({
              ...x,
              path: `pages.${m[1]}.${m[2]}.${x.path}`,
            }))
          )
      : expandInstances(content, f.path);
    for (const h of hits) {
      if (leafHasPlaceholder(h.value)) out.push({ path: h.path, label: f.label });
    }
  }
  return out;
}

/** `bindings[path]` 的 sourceRef → 字段旁的来源 chip(附录 A.1 / A.20);认不出的前缀不画。 */
export function bindingSourceText(ref: string | undefined | null): string | null {
  if (!ref) return null;
  if (ref.startsWith("kb:")) return "来自资料";
  if (ref.startsWith("user:")) return "你填写的";
  if (ref.startsWith("ai:")) return "AI 起草";
  return null;
}

/** 「站点基础」固定组:模板里所有 `brand.*` 字段。 */
export function brandFields(template: Pick<Template, "schema">): FieldSpec[] {
  return template.schema.flatMap((g) => g.fields.filter((f) => f.path.startsWith("brand.")));
}

export interface BlockRepeat {
  /** props 里的列表键(`items`)。 */
  listKey: string;
  /** 组的 repeat.max;模板没写给 null(不封顶)。 */
  max: number | null;
  /** 每一项的子字段(key 是相对键 `title` / `desc`)。 */
  fields: FieldSpec[];
}

export interface BlockFields {
  /** `pages.{slug}.{type}.` */
  prefix: string;
  /** 标量字段(key 是去掉前缀后的相对路径:`title` / `cta.label` / `image`)。 */
  scalars: FieldSpec[];
  repeat: BlockRepeat | null;
}

/**
 * 某页某区块的可编辑字段:模板 schema 里 `path` 以 `pages.{slug}.{type}.` 开头的那些。
 * 相对路径含 `[].` 的归入重复组(`items[].title` → listKey `items`、子键 `title`),上限取所在组的 `repeat.max`。
 * ★ 模板只给 home 写了字段的话,别的页同型区块就是没字段 —— 这里不替它推。
 */
export function blockFields(
  template: Pick<Template, "schema">,
  slug: string,
  type: string
): BlockFields {
  const prefix = `pages.${slug}.${type}.`;
  const scalars: FieldSpec[] = [];
  let repeat: BlockRepeat | null = null;
  for (const g of template.schema) {
    for (const f of g.fields) {
      if (!f.path.startsWith(prefix)) continue;
      const rel = f.path.slice(prefix.length);
      const at = rel.indexOf("[].");
      if (at < 0) {
        scalars.push({ ...f, key: rel });
        continue;
      }
      const listKey = rel.slice(0, at);
      const key = rel.slice(at + 3);
      if (!repeat) repeat = { listKey, max: g.repeat?.max ?? null, fields: [] };
      if (repeat.listKey === listKey) repeat.fields.push({ ...f, key });
    }
  }
  return { prefix, scalars, repeat };
}

/** `cta.label` / `items[0].title` → ["cta","label"] / ["items","0","title"]。 */
function segments(path: string): string[] {
  return path
    .replace(/\[(\d+)\]/g, ".$1")
    .split(".")
    .filter(Boolean);
}

/** 点路径读值(数组下标写成 `items[0].title`);路上断了回 undefined。 */
export function getAt(obj: unknown, path: string): unknown {
  let cur: unknown = obj;
  for (const seg of segments(path)) {
    if (cur == null || typeof cur !== "object") return undefined;
    cur = (cur as Record<string, unknown>)[seg];
  }
  return cur;
}

/** 点路径写值,返回新对象(沿路浅拷贝,其余引用不变);缺的中间层按下一段是不是数字补对象 / 数组。 */
export function setAt<T>(obj: T, path: string, value: unknown): T {
  const segs = segments(path);
  const write = (cur: unknown, i: number): unknown => {
    if (i === segs.length) return value;
    const seg = segs[i];
    const nextIsIndex = /^\d+$/.test(segs[i + 1] ?? "");
    const base: unknown =
      cur != null && typeof cur === "object" ? cur : nextIsIndex || /^\d+$/.test(seg) ? [] : {};
    if (Array.isArray(base)) {
      const copy = base.slice();
      copy[Number(seg)] = write(base[Number(seg)], i + 1);
      return copy;
    }
    const rec = base as Record<string, unknown>;
    return { ...rec, [seg]: write(rec[seg], i + 1) };
  };
  return write(obj, 0) as T;
}

/** 一次改动的来源:用户手改 `user:edit`,AI 重写 `ai:rewrite`(附录 A.1 / A.30;事实字段只读,走不到这里)。 */
export type EditRef = "user:edit" | "ai:rewrite";

const BLOCK_PATH = /^pages\.([^.]+)\.([^.]+)\.(.+)$/;

/**
 * 把一格的新值写进 site.json(纯函数,编辑器的 `applyAt` 在 `setContent(prev => …)` 里调它,永远写在最新那份上):
 *  - `pages.{slug}.{type}.{rel}` → 该页 id 为 `blockId` 的区块(没给 id 就取该页第一个同型区块)的 `props.{rel}`;
 *  - 其余(`brand.*` 等)→ 按路径直接写;
 *  - `bindings[path]` 记来源(实例路径,附录 A.30)。
 * 找不到区块时内容原样、只记绑定 —— 不替它造一块。
 */
export function applyEdit(
  content: SiteContent,
  path: string,
  value: unknown,
  ref: EditRef,
  blockId?: string
): SiteContent {
  const bindings = { ...content.bindings, [path]: ref };
  const m = BLOCK_PATH.exec(path);
  if (!m) return { ...setAt(content, path, value), bindings };
  const [, slug, type, rel] = m;
  let hit = false;
  const pages = content.pages.map((p) => {
    if (p.slug !== slug || hit) return p;
    const target = p.blocks.find((b) => (blockId ? b.id === blockId : b.type === type));
    if (!target) return p;
    hit = true;
    return {
      ...p,
      blocks: p.blocks.map((b) => (b !== target ? b : { ...b, props: setAt(b.props, rel, value) })),
    };
  });
  return { ...content, pages, bindings };
}

/** 改某页某区块的 props / bindings(重复组加项 / 删项用);找不到区块原样返回。 */
export function updateBlock(
  content: SiteContent,
  slug: string,
  blockId: string,
  fn: (
    props: Record<string, unknown>,
    bindings: Record<string, string>
  ) => {
    props: Record<string, unknown>;
    bindings: Record<string, string>;
  }
): SiteContent {
  const page = content.pages.find((p) => p.slug === slug);
  const block = page?.blocks.find((b) => b.id === blockId);
  if (!page || !block) return content;
  const next = fn(block.props, content.bindings);
  return {
    ...content,
    pages: content.pages.map((p) =>
      p !== page
        ? p
        : { ...p, blocks: p.blocks.map((b) => (b !== block ? b : { ...b, props: next.props })) }
    ),
    bindings: next.bindings,
  };
}

/** 某页里一块上 / 下移一格;越界或找不到原样返回(同一引用,调用方据此不置脏)。 */
export function moveBlock(
  content: SiteContent,
  slug: string,
  blockId: string,
  dir: -1 | 1
): SiteContent {
  const pi = content.pages.findIndex((p) => p.slug === slug);
  if (pi < 0) return content;
  const blocks = content.pages[pi].blocks;
  const bi = blocks.findIndex((b) => b.id === blockId);
  const to = bi + dir;
  if (bi < 0 || to < 0 || to >= blocks.length) return content;
  const next = blocks.slice();
  [next[bi], next[to]] = [next[to], next[bi]];
  const pages = content.pages.slice();
  pages[pi] = { ...pages[pi], blocks: next };
  return { ...content, pages };
}

/**
 * 删掉重复组第 `index` 项后,`bindings` 里 `{base}[i]…` 的键跟着挪:被删的那项丢掉,后面的下标减一。
 * 不做这一步,`items[2].title` 的来源会贴到删完后顶上来的那一项上。
 */
export function removeRepeatBindings(
  bindings: Record<string, string>,
  base: string,
  index: number
): Record<string, string> {
  const out: Record<string, string> = {};
  const head = `${base}[`;
  for (const [key, ref] of Object.entries(bindings)) {
    if (!key.startsWith(head)) {
      out[key] = ref;
      continue;
    }
    const m = /^\[(\d+)\](.*)$/.exec(key.slice(base.length));
    if (!m) {
      out[key] = ref;
      continue;
    }
    const i = Number(m[1]);
    if (i === index) continue;
    out[i > index ? `${base}[${i - 1}]${m[2]}` : key] = ref;
  }
  return out;
}
